import { useEffect, useState } from "react";
import { Check, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const [b, e1, e2] = await Promise.all([
      api.get("/admin/bookings" + (filter ? `?event_id=${filter}` : "")),
      api.get("/events?past=false"),
      api.get("/events?past=true"),
    ]);
    setBookings(b.data);
    setEvents([...e1.data, ...e2.data]);
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    await api.put(`/admin/bookings/${id}/approve`);
    toast.success("Booking approved");
    load();
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    await api.put(`/admin/bookings/${id}/cancel`);
    toast.success("Booking cancelled");
    load();
  };

  const exportCsv = async () => {
    const url = `${api.defaults.baseURL}/admin/bookings/export${filter ? `?event_id=${filter}` : ""}`;
    const token = localStorage.getItem("furshala_admin_token");
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "furshala-bookings.csv";
    a.click();
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-brand-soft">Bookings</h1>
          <p className="text-sm text-brand-soft/60 mt-1">Approve payments and manage cancellations.</p>
        </div>
        <div className="flex gap-3 items-center">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-white border border-brand-maroon/10 rounded-full px-4 py-2 text-sm" data-testid="bookings-filter">
            <option value="">All events</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
          <button onClick={exportCsv} className="btn-outline-furshala inline-flex items-center gap-2 text-sm py-2" data-testid="bookings-export">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-maroon/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-ivory text-brand-soft/70">
              <tr>
                <Th>Parent</Th>
                <Th>Event</Th>
                <Th>Total</Th>
                <Th>Status</Th>
                <Th>Vaccination</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-brand-soft/60 italic">No bookings yet.</td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-brand-maroon/5 align-top" data-testid={`booking-row-${b.id}`}>
                  <Td>
                    <div className="font-medium">{b.pet_parent_name}</div>
                    <div className="text-xs text-brand-soft/60">{b.email}</div>
                    <div className="text-xs text-brand-soft/60">{b.whatsapp_number}</div>
                    <div className="text-xs text-brand-soft/60 mt-1">{b.humans_count}h · {b.dogs_count}🐶 · {b.food_type}</div>
                    {Array.isArray(b.breakdown) && b.breakdown.length > 0 && (
                      <ul className="mt-2 text-[11px] text-brand-soft/70 border-t border-brand-maroon/10 pt-1.5 space-y-0.5">
                        {b.breakdown.map((row, i) => (
                          <li key={i} className="flex items-baseline justify-between gap-2">
                            <span className="truncate">{row.label}{row.qty > 1 ? ` × ${row.qty}` : ""}</span>
                            <span className={`font-medium ${row.amount < 0 ? "text-brand-olive" : "text-brand-soft"}`}>₹{row.amount}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Td>
                  <Td><div className="text-sm">{b.event_title}</div></Td>
                  <Td>
                    <div className="font-medium">₹{b.total_amount}</div>
                    <div className="text-xs text-brand-soft/60">{b.payment_ref}</div>
                  </Td>
                  <Td>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      b.payment_status === "approved" ? "bg-brand-olive/20 text-brand-olive" :
                      b.payment_status === "cancelled" ? "bg-red-100 text-red-700" : "bg-brand-mustard/20 text-brand-maroon"
                    }`}>{b.payment_status}</span>
                  </Td>
                  <Td>
                    {b.vaccination_drive_link ? <a href={b.vaccination_drive_link} target="_blank" rel="noreferrer" className="text-xs underline text-brand-maroon break-all">View link</a> : <span className="text-xs text-brand-soft/40">—</span>}
                  </Td>
                  <Td><div className="text-xs text-brand-soft/60">{new Date(b.created_at).toLocaleDateString()}</div></Td>
                  <Td>
                    {b.payment_status !== "approved" && b.payment_status !== "cancelled" && (
                      <button onClick={() => approve(b.id)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-olive/15 text-brand-olive hover:bg-brand-olive/25 mr-2" data-testid={`booking-approve-${b.id}`}>
                        <Check className="w-3 h-3" /> Approve
                      </button>
                    )}
                    {b.payment_status !== "cancelled" && (
                      <button onClick={() => cancel(b.id)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100">
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;
